import { useOrganizations } from '@contexts/organizations-provider';
import { useHasPermission } from '@contexts/permissions-provider';
import { DocumentsContent } from '@features/documents/components/documents-content/DocumentsContent';
import { DocumentsHeader } from '@features/documents/components/documents-header/DocumentsHeader';
import { DocumentsSearchView } from '@features/documents/components/documents-search-view/DocumentsSearchView';
import * as React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import styles from './CompanyDocumentsPage.module.css';

const CompanyDocumentsPage = React.memo((): React.ReactElement => {
  const { selectedOrganization } = useOrganizations();
  const canSubmit = useHasPermission('CustomerTrainingDocuments_Add_Org');
  const canDownload = useHasPermission('CustomerTrainingDocuments_Open_Org');
  const [isSearchActive, setIsSearchActive] = React.useState(false);
  const navigate = useNavigate();

  const organizationId = selectedOrganization?.id;

  const handleSubmitClick = React.useCallback((): void => {
    navigate('submit-documents');
  }, [navigate]);

  const handleSearchActivate = React.useCallback((): void => {
    setIsSearchActive(true);
  }, []);

  const handleSearchBack = React.useCallback((): void => {
    setIsSearchActive(false);
  }, []);

  if (isSearchActive) {
    return (
      <DocumentsSearchView
        title="Search"
        documentType="Customer"
        organizationId={organizationId}
        canDownload={canDownload}
        onBack={handleSearchBack}
      />
    );
  }

  return (
    <div
      data-testid="company-documents-page"
      className={styles.companyDocumentsPage}
    >
      <DocumentsHeader
        title={selectedOrganization?.name ?? 'Documents'}
        showSubmitButton={canSubmit}
        onSubmitClick={handleSubmitClick}
        onSearchActivate={handleSearchActivate}
      />
      <DocumentsContent
        documentType="Customer"
        organizationId={organizationId}
        canDownload={canDownload}
        rootLabel={selectedOrganization?.name ?? 'Documents'}
      />
      <Outlet />
    </div>
  );
});

CompanyDocumentsPage.displayName = 'CompanyDocumentsPage';

export { CompanyDocumentsPage };
